<template>
  <div>
    <div
      v-if="showTotalBadge"
      class="pagination-wrapper top"
    >
      <div class="pagination-info">
        <span class="total-badge">{{ pagination.total }} {{ getItemLabel() }}</span>
        <div class="page-size-selector">
          <span class="page-size-label">Show:</span>
          <AIVSelect
            :options="pageSizeOptions"
            :value="pagination.perPage"
            @input="onPageSizeChange"
            :searchable="false"
            :clearable="false"
            :custom-style="{
              width: '70px',
              height: '28px',
              fontSize: '12px',
              backgroundColor: '#656565',
              border: '1px solid #bdbdbd'
            }"
            placeholder=""
          />
        </div>
      </div>
      <div v-if="showNavigationControls" class="pagination-controls">
        <div class="flex items-center gap-1">
          <button
            class="pagination-btn"
            @click="pageChange(1)"
            :disabled="pagination.currentPage === 1"
          >
            <i class="fas fa-angle-double-left"></i>
          </button>
          <button
            class="pagination-btn"
            @click="pageChange(pagination.currentPage - 1)"
            :disabled="pagination.currentPage === 1"
          >
            <i class="fas fa-angle-left"></i>
          </button>

          <span class="pagination-current">
            Page {{ pagination.currentPage }} of {{ pagination.totalPages }}
          </span>

          <button
            class="pagination-btn"
            @click="pageChange(pagination.currentPage + 1)"
            :disabled="pagination.currentPage === pagination.totalPages"
          >
            <i class="fas fa-angle-right"></i>
          </button>
          <button
            class="pagination-btn"
            @click="pageChange(pagination.totalPages)"
            :disabled="pagination.currentPage === pagination.totalPages"
          >
            <i class="fas fa-angle-double-right"></i>
          </button>

          <div class="go-to-page">
            <span class="go-to-label">Go to:</span>
            <input
              ref="goToPageInput"
              type="number"
              v-model.number="goToPageInput"
              @keyup.enter="goToPage"
              @blur="onGoToPageBlur"
              @focus="onGoToPageFocus"
              :min="1"
              :max="pagination.totalPages"
              class="go-to-input"
              :placeholder="isGoToPageFocused ? '' : 'Page'"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="table-container">
      <table
        :class="['aiv-table', 'table-striped', { 'modal-table': modalTable }]"
      >
        <thead>
          <slot name="header">
            <tr>
              <th
                v-for="column in filteredColumns"
                :key="column.key"
                @click="column.sortable === false ? null : sortBy(column.key)"
                :class="[
                  column.class,
                  {
                    'sorted-asc': sortKey === column.key && sortOrder === 'asc',
                    'sorted-desc': sortKey === column.key && sortOrder === 'desc',
                    'sortable': column.sortable !== false,
                  }
                ]"
              >
<span v-html="column.label"></span><span v-if="sortKey === column.key"> {{ sortOrder === "asc" ? "▲" : "▼" }}</span>
              </th>
            </tr>
          </slot>
        </thead>
        <tbody>
          <tr
            v-for="(row, index) in sortedData"
            :key="index"
            :class="[rowClass ? rowClass(row) : '', rowClickable ? 'row-clickable' : '']"
            @click="onRowClick(row)"
          >
            <td
              v-for="column in filteredColumns"
              :key="column.key"
              :class="column.class"
              :data-label="plainLabel(column)"
            >
              <slot :name="`cell-${column.key}`" :row="row" :column="column">
                <div v-if="column.component">
                  <span v-if="column.componentProps(row).title">
                    {{ column.componentProps(row).title }}
                  </span>
                  <br v-if="column.componentProps(row).title" />
                  <component
                    v-if="column.component && column.component !== 'select'"
                    :is="column.component"
                    v-bind="
                      column.componentProps ? column.componentProps(row) : {}
                    "
                    @click="
                      column.componentProps
                        ? column.componentProps(row).onClick?.(row)
                        : null
                    "
                    component=""
                  />
                </div>
                <select
                  v-else-if="column.component === 'select'"
                  v-model="row[column.key]"
                  @change="(e) => (row[column.key] = e.target.value)"
                  v-select-min-width="column.componentProps(row).options"
                >
                  <option
                    v-for="option in column.componentProps(row).options"
                    :key="option.id"
                    :value="option.id"
                  >
                    {{ option.name }}
                  </option>
                </select>
                <span
                  v-else-if="column.isHtml"
                  v-html="
                    column.value
                      ? column.value(row)
                      : column.format
                        ? column.format(row[column.key])
                        : row[column.key]
                  "
                ></span>
                <span v-else>
                  {{
                    column.format
                      ? column.format(row[column.key])
                      : row[column.key]
                  }}
                </span>
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import CommonAPIService from "src/serviceHandlers/commonHandler";
import AIVSelect from "./AIVSelect.vue";

const apiService = new CommonAPIService();

export default {
  components: {
    AIVSelect,
  },
  props: {
    columns: { type: Array, required: true },
    data: { type: Array, required: true },
    modalTable: { type: Boolean, default: false },
    rowClass: { type: Function, default: null },
    rowClickable: { type: Boolean, default: false },
    pagination: {
      type: Object,
      default: () => ({ total: 0, totalPages: 0, currentPage: 0, perPage: 0 }),
    },
    tableName: { type: String, default: "contacts" },
    formatData: { type: Function, default: null },
    itemName: { type: String, default: "items" },
  },
  data() {
    return {
      sortKey: "",
      sortOrder: "asc",
      searchQuery: "",
      pageSizeOptions: [
        { id: 20, name: "20" },
        { id: 50, name: "50" },
        { id: 100, name: "100" }
      ],
      goToPageInput: null,
      isGoToPageFocused: false
    };
  },
  computed: {
    showTotalBadge() {
      // Always show total badge when there are items
      return this.pagination?.total > 0;
    },
    showNavigationControls() {
      // Show navigation controls only if there are more than 10 total items
      return this.pagination?.total > 10;
    },
    filteredColumns() {
      return this.columns.filter((column) => {
        if (typeof column.condition === "function") {
          return column.condition();
        }
        return true;
      });
    },
    sortedData() {
      // Skip client-side sorting when parent handles sorting server-side
      if (!this.sortKey || this.$listeners['sort-change']) return this.data;

      return [...this.data].sort((a, b) => {
        let aValue = this.getNestedValue(a, this.sortKey);
        let bValue = this.getNestedValue(b, this.sortKey);

        if (typeof aValue === "string" && aValue.startsWith("$")) {
          aValue = parseFloat(aValue.replace(/[^0-9.-]+/g, ""));
        }
        if (typeof bValue === "string" && bValue.startsWith("$")) {
          bValue = parseFloat(bValue.replace(/[^0-9.-]+/g, ""));
        }

        if (!isNaN(parseFloat(aValue)) && !isNaN(parseFloat(bValue))) {
          aValue = parseFloat(aValue);
          bValue = parseFloat(bValue);
        }

        if (aValue === bValue) return 0;

        if (this.sortOrder === "asc") {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });
    },
  },
  methods: {
    plainLabel(column) {
      // Labels may contain HTML (rendered via v-html in the header);
      // the mobile data-label attribute needs plain text
      return (column.label || "")
        .replace(/<[^>]*>/g, " ")
        .replace(/\s+/g, " ")
        .trim();
    },
    onRowClick(row) {
      if (this.rowClickable) {
        this.$emit('row-click', row);
      }
    },
    getItemLabel() {
      // If itemName is not provided or not a string, use default
      if (!this.itemName || typeof this.itemName !== 'string') {
        return 'items';
      }

      // Handle singular forms when count is 1
      if (this.pagination?.total === 1) {
        // Handle special cases for singular forms
        if (this.itemName === 'employees') {
          return 'employee';
        } else if (this.itemName.endsWith('ies')) {
          // e.g., "categories" -> "category"
          return this.itemName.slice(0, -3) + 'y';
        } else if (this.itemName.endsWith('es')) {
          // e.g., "offices" -> "office", "addresses" -> "address"
          return this.itemName.slice(0, -2);
        } else if (this.itemName.endsWith('s')) {
          // e.g., "shifts" -> "shift", "clients" -> "client"
          return this.itemName.slice(0, -1);
        }
      }
      return this.itemName;
    },
    getNestedValue(obj, key) {
      return key.split(".").reduce((o, i) => (o ? o[i] : null), obj);
    },
    sortBy(key) {
      const column = this.filteredColumns.find((col) => col.key === key);
      if (column?.sortable === false) return;

      const sortField = column?.sortKey || key;
      if (this.sortKey === key) {
        this.sortOrder = this.sortOrder === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;
        this.sortOrder = "asc";
      }
      this.$emit("sort-change", {
        prop: sortField,
        order: this.sortOrder,
      });
    },
    pageChange(newPage) {
      this.$emit('pageChange', newPage);
      this.$root.$emit('scroll-to-top');
    },
    onPageSizeChange(newSize) {
      this.$emit('pageSizeChange', newSize);
    },
    onGoToPageFocus() {
      // Set focus state and clear the input when user focuses on it
      this.isGoToPageFocused = true;
      this.goToPageInput = null;
    },
    onGoToPageBlur() {
      // Set focus state to false and trigger navigation
      this.isGoToPageFocused = false;
      this.goToPage();
    },
    goToPage() {
      if (!this.goToPageInput) {
        return;
      }

      let targetPage = parseInt(this.goToPageInput);

      // Validate the page number
      if (isNaN(targetPage) || targetPage < 1) {
        targetPage = 1;
      } else if (targetPage > this.pagination.totalPages) {
        targetPage = this.pagination.totalPages;
      }

      // Only navigate if it's a different page
      if (targetPage !== this.pagination.currentPage) {
        this.pageChange(targetPage);
      }

      // Clear the input and remove focus after navigation
      this.goToPageInput = null;
      this.isGoToPageFocused = false;

      // Remove focus from the input element
      if (this.$refs.goToPageInput) {
        this.$refs.goToPageInput.blur();
      }
    },
  },
  directives: {
    selectMinWidth: {
      inserted(el, binding) {
        const options = binding.value;
        const tempDiv = document.createElement("div");
        tempDiv.style.position = "absolute";
        tempDiv.style.visibility = "hidden";
        tempDiv.style.height = "auto";
        tempDiv.style.width = "auto";
        tempDiv.style.whiteSpace = "nowrap";
        document.body.appendChild(tempDiv);

        let maxWidth = 0;
        options.forEach((option) => {
          tempDiv.textContent = option;
          const width = tempDiv.clientWidth;
          if (width > maxWidth) {
            maxWidth = width;
          }
        });

        document.body.removeChild(tempDiv);
        el.style.minWidth = `${maxWidth + 40}px`;
      },
    },
  },
};
</script>

<style scoped>
.aiv-table {
  border-collapse: separate;
  border-spacing: 0;
  border-radius: 10px;
  overflow-x: auto;
  background-color: rgba(37, 55, 65, 0.7);
  color: #e0e0e0;
  border: 1px solid rgba(66, 98, 116, 0.5);
  box-shadow: 0 8px 10px rgba(0, 0, 0, 0.3);
  min-width: max-content;
  width: 100%;
  -webkit-overflow-scrolling: touch;
}
.aiv-table th,
.aiv-table td {
  padding: 14px 10px;
  border: 1px solid rgba(66, 98, 116, 0.5);
  text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.1);
}
.aiv-table th {
  font-weight: 100;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  text-transform: uppercase;
  font-size: 16px;
  color: #e0e0e0;
}

.aiv-table th.sortable {
  cursor: pointer;
  user-select: none;
  position: relative;
}

.aiv-table th.sortable:hover {
  background-color: rgba(66, 98, 116, 0.3);
}

.aiv-table th.sorted-asc,
.aiv-table th.sorted-desc {
  background-color: rgba(66, 98, 116, 0.4);
}

.aiv-table th span + span {
  margin-left: 5px;
  font-size: 12px;
}
.aiv-table th:first-child {
  border-top-left-radius: 8px;
}
.aiv-table th:last-child {
  border-top-right-radius: 8px;
}
.aiv-table tr:last-child td:first-child {
  border-bottom-left-radius: 8px;
}
.aiv-table tr:last-child td:last-child {
  border-bottom-right-radius: 8px;
}

.aiv-table tr.row-clickable {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.aiv-table tr.row-clickable:hover {
  background-color: rgba(66, 98, 116, 0.3);
}

/* Phones: each row becomes a labeled card — the header row is hidden
   and every cell shows its column label on the left (data-label) */
@media (max-width: 768px) {
  .aiv-table {
    min-width: 0;
    background: transparent;
    border: none;
    box-shadow: none;
  }

  .aiv-table thead {
    display: none;
  }

  .aiv-table,
  .aiv-table tbody,
  .aiv-table tr,
  .aiv-table td {
    display: block;
    width: 100%;
  }

  .aiv-table tr {
    margin-bottom: 12px;
    background-color: rgba(37, 55, 65, 0.7);
    border: 1px solid rgba(66, 98, 116, 0.5);
    border-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.25);
    padding: 4px 0;
  }

  .aiv-table td {
    text-align: left;
    padding: 8px 10px 8px 42%;
    position: relative;
    min-height: 36px;
    border: none;
    border-bottom: 1px solid rgba(66, 98, 116, 0.25);
    border-radius: 0 !important;
  }

  .aiv-table td:last-child {
    border-bottom: none;
  }

  .aiv-table td::before {
    content: attr(data-label);
    position: absolute;
    top: 8px;
    left: 10px;
    width: 38%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    font-weight: bold;
    color: #ccc;
  }

  /* A cell without a label (e.g. custom header slots) gets full width */
  .aiv-table td:not([data-label]),
  .aiv-table td[data-label=""] {
    padding-left: 10px;
  }

  /* Compact slotted action buttons so a full set of 5 fits on one row
     (mr-2 is !important in Bootstrap — match it to win on specificity) */
  .aiv-table td :deep(.btn) {
    padding: 3px 6px;
    font-size: 12px;
    margin-right: 3px !important;
  }
}
/*noinspection CssUnusedSymbol */
.modal-table {
  /* Inherits all .aiv-table styles — no overrides needed */
}
.table-container {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}
.table-container::-webkit-scrollbar {
  height: 6px;
}
.table-container::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.3);
  border-radius: 3px;
}

/* Modern Pagination Styles */
.pagination-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
}

.pagination-wrapper.top {
  margin-bottom: 6px;
}

.pagination-info {
  display: flex;
  align-items: center;
  gap: 20px;
  font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif;
}

.page-size-selector {
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-size-label {
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.total-badge {
  display: inline-block;
  background-color: #e2e8f0;
  color: #475569;
  font-size: 11px;
  font-weight: 500;
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid #cbd5e1;
}

.pagination-info .page-info {
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
}

.pagination-controls {
  display: flex;
  align-items: center;
}

.flex {
  display: flex;
}

.items-center {
  align-items: center;
}

.gap-1 {
  gap: 0.25rem;
}

.pagination-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 24px;
  width: 24px;
  padding: 0;
  border: 1px solid #d1d5db;
  background-color: white;
  color: #374151;
  font-size: 10px;
  border-radius: 3px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
}

.pagination-btn:hover:not(:disabled) {
  background-color: #f9fafb;
  border-color: #9ca3af;
}

.pagination-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  color: #9ca3af;
}

.pagination-current {
  font-size: 12px;
  color: #ffffff;
  margin: 0 10px;
  font-weight: 500;
}

.go-to-page {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-left: 16px;
}

.go-to-label {
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
}

.go-to-input {
  width: 50px;
  height: 28px;
  padding: 0 6px;
  border: 1px solid #bdbdbd;
  background-color: #656565;
  color: #ffffff;
  font-size: 12px;
  border-radius: 3px;
  text-align: center;
  transition: all 0.15s ease-in-out;
  line-height: 26px;
  vertical-align: middle;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Remove spinner arrows from number input */
.go-to-input::-webkit-outer-spin-button,
.go-to-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.go-to-input[type=number] {
  -moz-appearance: textfield;
}

.go-to-input:focus {
  outline: none;
  border-color: #9ca3af;
  background-color: #656565;
}

.go-to-input::placeholder {
  color: #ffffff;
  font-size: 12px;
}

/* Column visibility classes */
@media (max-width: 1200px) {
  .hide-on-small {
    display: none !important;
  }
}

@media (max-width: 1600px) {
  .hide-on-medium {
    display: none !important;
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pagination-wrapper {
    flex-direction: column;
    gap: 12px;
    padding: 12px 0;
  }

  .pagination-info {
    width: 100%;
    justify-content: center;
  }

  .pagination-controls {
    width: 100%;
    justify-content: center;
  }

  .pagination-info .total-items,
  .pagination-info .page-info {
    font-size: 12px;
  }

  .go-to-page {
    margin-left: 0;
    margin-top: 8px;
  }

  .go-to-input {
    width: 45px;
    height: 22px;
    font-size: 10px;
  }
}
</style>
